import * as React from 'react';
import { format, differenceInDays } from 'date-fns';

import './styles/price_calculator';
import DatePicker from '../Calendar/DatePicker';
import Price from '../Shared/Price';
import { Field, FieldGroup, FieldGroupLabel } from '../Shared/Fields';
import InsuranceCartButton from '../ShoppingCart/InsuranceCartButton';
import t from './locales';
import { formatDateWithDayOfWeek } from '../utils/dateHelper';

import { IPrice, IPeople, IInsurancePlan, IInsurancePlans, IInsuranceAllPlans, IResort, ICurrency } from '../ShoppingCart/types';

/* Types  */

interface IProps {
  itemId: string;
  itemName: string;
  insuranceAllPlans: IInsuranceAllPlans;
  minDateTime: Date;
  resort?: IResort;
  localCurrency?: ICurrency;
}

interface IState {
  isCalendarShown: boolean;
  people: IPeople;
  selectedDateList: Date[];
}

/* Internal components & functions  */

function insuranceLastKeyNumber(insurancePlans: IInsurancePlans): number {
  const planKeys = Object.keys(insurancePlans);
  return Number(planKeys[planKeys.length - 1] || 0);
}

function insuranceDuration(selectedDateList: Date[]): number {
  const firstDate = selectedDateList[0];
  const lastDate = selectedDateList[selectedDateList.length - 1];
  if (!firstDate) {
    return 0;
  } else if (!lastDate) {
    return 1;
  } else {
    return differenceInDays(lastDate, firstDate) + 1;
  }
}

function isValidInsuranceDate(insurancePlans: IInsurancePlans, selectedDateList: Date[]): boolean {
  const duration = insuranceDuration(selectedDateList);
  return duration > 0 && duration <= insuranceLastKeyNumber(insurancePlans)
}

function findInsurancePlan(insurancePlans: IInsurancePlans, selectedDateList: Date[]): IInsurancePlan|null {
  if (!isValidInsuranceDate(insurancePlans, selectedDateList)) {
    return null;
  }
  let planKey = insuranceDuration(selectedDateList);
  let plan = null;
  while (plan == null) {
    plan = insurancePlans[String(planKey)];
    planKey += 1;
  }
  return plan;
}

function calculateInsurancePrice(itemId: string, insuranceAllPlans: IInsuranceAllPlans, selectedDateList: Date[]): number {
  const plans: IInsurancePlan[] = itemId.split(',').map(insuranceType => (
    findInsurancePlan(insuranceAllPlans[insuranceType], selectedDateList)
  )).filter(plan => !!plan);
  let price = 0;
  plans.forEach(plan => price += plan.price);
  return price;
}

const NumberSelect = ({ name, handleChange, selectedNumber }:
  { name: string, handleChange: Function, selectedNumber: number }) => {
  const limit = 10;
  const options = [];
  for (let i = 0; i <= limit; i++) {
    options.push(<option key={`${name}-${i}`}>{i}</option>);
  }
  return (
    <select onChange={(e: any) => { handleChange(e); }} value={selectedNumber}>
      {options}
    </select>
  );
}

/* Component  */

class InsurancePriceCalculator extends React.Component<IProps, IState> {

  state: IState = {
    isCalendarShown: false,
    people: {
      adultNum: 0,
      childNum: 0,
      studentNum: 0,
      seniorNum: 0,
    },
    selectedDateList: [],
  }

  handleNumberOfPeopleChange(name: string) {
    return (event: React.ChangeEvent) => {
      const index = (event.currentTarget as any).selectedIndex;
      const { people } = this.state;
      people[name] = index;
      this.setState({ people });
    }
  }

  handleDateListChange(selectedDateList: Date[]): void {
    this.setState({ selectedDateList });
  }

  toggleCalendarModal(event: React.MouseEvent): void {
    event.preventDefault();
    const isCalendarShown = !this.state.isCalendarShown;
    // Hack: 既存DOMとの上下関係をコントロール
    document.getElementById('cta-container').style.zIndex = `${isCalendarShown ? 1049 : 1}`;
    this.setState({ isCalendarShown });
  }

  insurancePricePerPerson(): number {
    const { itemId, insuranceAllPlans } = this.props;
    const { selectedDateList } = this.state;
    return calculateInsurancePrice(itemId, insuranceAllPlans, selectedDateList);
  }

  renderDateInput() {
    const dateFormat = 'yyyy.MM.dd';
    const { selectedDateList } = this.state;
    const firstDate = selectedDateList[0];
    const numberOfDate = selectedDateList.length;
    let inputString = '';
    if (numberOfDate === 0) {
      inputString = t.pleaseSelect();
    }
    if (numberOfDate === 1) {
      inputString = formatDateWithDayOfWeek(firstDate);
    }
    if (numberOfDate > 1) {
      const lastDate = selectedDateList[numberOfDate - 1];
      inputString = `${formatDateWithDayOfWeek(firstDate)} - ${formatDateWithDayOfWeek(lastDate)}`;
    }
    return (
      <FieldGroup>
        <FieldGroupLabel>{t.dateOfUse()}</FieldGroupLabel>
        <Field noLeftBorder>
          <input value={inputString} readOnly onClick={this.toggleCalendarModal.bind(this)} />
        </Field>
      </FieldGroup>
    );
  }

  renderPriceSummary() {
    const { people } = this.state;
    const insurancePrice = this.insurancePricePerPerson();
    if (insurancePrice === 0 || people.adultNum === 0) { return null; }
    return (
      <Price price={insurancePrice * people.adultNum} style={{ marginRight: '0.5em' }} localCurrency={this.props.localCurrency} />
    );
  }

  renderCloseButton() {
    return (
      <div>
        <a href="#" className="price-calculator__button" onClick={this.toggleCalendarModal.bind(this)}>
          OK
        </a>
      </div>
    );
  }

  renderCalendarModal(isShown: boolean = false) {
    return (
      <div
        className={`price-calculator-modal ${isShown ? 'active': ''}`}
        onClick={this.toggleCalendarModal.bind(this)}>
        <div className="price-calculator-modal__inner" onClick={(e) => { e.stopPropagation(); }}>
          <DatePicker
            isRangeSelect
            handleDateListChange={this.handleDateListChange.bind(this)}
            minDate={this.props.minDateTime}
            maxRange={30}
          />
          <div className="price-calculator-modal__footer">
            {this.renderPriceSummary()}
            {this.renderCloseButton()}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isCalendarShown, selectedDateList } = this.state;
    const insurancePrice = this.insurancePricePerPerson();
    const price: IPrice = {
      adultPrice: insurancePrice,
      childPrice: insurancePrice,
      studentPrice: insurancePrice,
      seniorPrice: insurancePrice,
      groupPrice: null,
      adultFullPrice: insurancePrice,
      childFullPrice: insurancePrice,
      groupFullPrice: null,
      studentFullPrice: insurancePrice,
      seniorFullPrice: insurancePrice,
    };
    return (
      <div>
        <div className="price-calculator__section">
          <div className="price-calculator__people">
            {['adultNum'].map((name: 'adultNum') => (
              <FieldGroup key={name} style={{ width: '48%' }}>
                <FieldGroupLabel>{t[name]()}</FieldGroupLabel>
                <Field className="dropdown" noLeftBorder>
                  <NumberSelect
                    name={name}
                    handleChange={this.handleNumberOfPeopleChange(name)}
                    selectedNumber={this.state.people[name]}
                  />
                </Field>
              </FieldGroup>
            ))}
          </div>
        </div>
        <div className="price-calculator__section">
          {this.renderDateInput()}
        </div>
        <div className="price-calculator__cta">
          {this.renderPriceSummary()}
          <InsuranceCartButton
            itemId={this.props.itemId}
            itemName={this.props.itemName}
            people={this.state.people}
            price={price}
            selectedDateList={selectedDateList}
            resort={this.props.resort}
          />
        </div>
        {this.renderCalendarModal(isCalendarShown)}
      </div>
    );
  }
}

export default InsurancePriceCalculator
